<template>
  <!-- Duplicate Turnaround Modal (DEV-220) — shared by ChecksStartView (create)
       and ChecksServicesView (add-services). State lives in useDuplicateTurnaround. -->
  <div v-if="show && data" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    <div class="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
      <div class="mb-4">
        <h3 class="text-lg font-semibold text-gray-900 mb-2">
          Turnaround bereits vorhanden
        </h3>
        <p class="text-sm text-gray-600">
          {{ data.message || data.details?.message }}
        </p>
      </div>

      <!-- Details of the previous turnaround -->
      <div class="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
        <div class="flex items-start">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-orange-500 mr-2 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div class="text-sm text-orange-800 space-y-1">
            <p v-if="data.details?.previous_checkin_at">
              <span class="font-semibold">Letzter Turnaround:</span>
              {{ formatDate(data.details.previous_checkin_at) }}
            </p>
            <p v-if="data.details?.previous_mileage != null">
              <span class="font-semibold">Kilometerstand damals:</span>
              {{ data.details.previous_mileage }} km
            </p>
            <p v-if="data.details?.mileage != null">
              <span class="font-semibold">Kilometerstand jetzt:</span>
              {{ data.details.mileage }} km
            </p>
            <p v-if="data.details?.delta_km != null">
              <span class="font-semibold">Differenz:</span>
              {{ data.details.delta_km }} km
            </p>
          </div>
        </div>
      </div>

      <!-- Mandatory reason -->
      <div v-if="needsReason" class="mb-4">
        <label for="duplicate-turnaround-reason" class="block text-sm font-medium text-gray-700 mb-1">
          Begründung <span class="text-[#E9041D]">*</span>
        </label>
        <textarea
          id="duplicate-turnaround-reason"
          :value="reason"
          @input="$emit('update:reason', ($event.target as HTMLTextAreaElement).value)"
          rows="3"
          maxlength="500"
          placeholder="Warum ist ein weiterer Turnaround nötig? (mind. 10 Zeichen)"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E9041D] text-sm"
        ></textarea>
        <p class="mt-1 text-xs text-gray-500">
          {{ reason.trim().length }}/10 Zeichen
        </p>
      </div>

      <!-- Error from a failed confirmation -->
      <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
        <p class="text-sm text-red-800">{{ error }}</p>
      </div>

      <div class="flex flex-col space-y-3">
        <button
          @click="$emit('confirm')"
          :disabled="!canConfirm"
          class="w-full px-4 py-3 text-white rounded-lg transition-colors font-medium"
          :class="canConfirm ? 'bg-[#E9041D] hover:bg-red-700' : 'bg-gray-300 cursor-not-allowed'"
        >
          {{ isConfirming ? busyLabel : confirmLabel }}
        </button>
        <button
          @click="$emit('close')"
          :disabled="isConfirming"
          class="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
        >
          Abbrechen
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DuplicateTurnaroundResponse } from '../composables/useDuplicateTurnaround'

withDefaults(defineProps<{
  show: boolean
  data: DuplicateTurnaroundResponse | null
  reason: string
  error: string
  isConfirming: boolean
  needsReason: boolean
  canConfirm: boolean
  confirmLabel?: string
  busyLabel?: string
}>(), {
  confirmLabel: 'Trotzdem anlegen',
  busyLabel: 'Wird angelegt...'
})

defineEmits<{
  (e: 'update:reason', value: string): void
  (e: 'confirm'): void
  (e: 'close'): void
}>()

const formatDate = (value?: string) => {
  if (!value) return ''
  const parsed = new Date(value.replace(' ', 'T'))
  if (isNaN(parsed.getTime())) return value
  return parsed.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}
</script>
